
import { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/auth';
import { useDataRefresh } from './use-data-refresh';
import { useAlerts } from './use-alerts';
import { useMigration } from './use-migration';

export const useDashboardData = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Initialize all the sub-hooks
  const { 
    dismissedAlerts, 
    alertSettings,
    handleDismissAlert, 
    handleAddStock 
  } = useAlerts();
  
  const {
    products,
    lowStockProducts,
    isLoading,
    setIsLoading,
    fetchError,
    dataFetchAttempt,
    incrementFetchAttempt,
    refreshData
  } = useDataRefresh(dismissedAlerts);
  
  const {
    isMigrating,
    showMigration,
    migrationAttempted,
    checkForLocalData,
    handleMigrateData
  } = useMigration(refreshData);

  // Load data with retry mechanism
  useEffect(() => {
    const loadUserData = async () => {
      if (!user) return;
      
      setIsLoading(true);
      console.log(`Dashboard loaded for user: ${user.email} (${user.id})`);
      
      // Check for local data that might need to be migrated
      checkForLocalData(user.id);
      
      // Attempt to refresh data - with a maximum timeout
      try {
        const loadingPromise = refreshData();
        
        // Add overall timeout for loading
        const timeoutPromise = new Promise<boolean>((_, reject) => {
          setTimeout(() => reject(new Error('Overall loading timeout')), 12000);
        });
        
        await Promise.race([loadingPromise, timeoutPromise]);
      } catch (error) {
        console.error("Failed to load dashboard data:", error);
        // Ensure we exit loading state
        setIsLoading(false);
      }
    };
    
    loadUserData();
  }, [navigate, user, migrationAttempted, refreshData, dataFetchAttempt, checkForLocalData, setIsLoading]);

  const handleUsageRecorded = useCallback(() => {
    refreshData();
  }, [refreshData]);

  return {
    products,
    lowStockProducts,
    dismissedAlerts,
    alertSettings,
    isMigrating,
    showMigration,
    isLoading,
    fetchError,
    handleMigrateData,
    handleDismissAlert,
    handleAddStock,
    handleUsageRecorded,
    refreshData
  };
};
